import { useCallback, useState } from 'react';
import { GRID_COLS, GRID_MAX_ROWS, GRID_ROW_HEIGHT, GRID_WIDTH } from '../constants';
import { GridLayoutItem } from '../types';

const CELL_WIDTH = GRID_WIDTH / GRID_COLS;
const CELL_HEIGHT = GRID_ROW_HEIGHT;

interface UseGridResizeOptions {
  item: GridLayoutItem;
  occupiedCells: boolean[][];
  onResizeComplete: (itemId: string, newW: number, newH: number) => void;
}

interface UseGridResizeReturn {
  isResizing: boolean;
  previewSize: { w: number; h: number } | null;
  handleResizeStart: () => void;
  handleResize: (deltaX: number, deltaY: number) => void;
  handleResizeEnd: (deltaX: number, deltaY: number) => void;
}

/**
 * Check if resizing to new dimensions is valid
 * (doesn't overlap other items or exceed grid bounds)
 */
function isResizeValid(
  item: GridLayoutItem,
  newW: number,
  newH: number,
  occupiedCells: boolean[][]
): boolean {
  // Check grid bounds
  if (item.x + newW > GRID_COLS || item.y + newH > GRID_MAX_ROWS) {
    return false;
  }

  // Check minimum size
  if (newW < 1 || newH < 1) {
    return false;
  }

  // Check if new area overlaps with other items
  // We need to check cells that would be newly occupied
  for (let row = item.y; row < item.y + newH; row++) {
    for (let col = item.x; col < item.x + newW; col++) {
      // Skip cells already occupied by this item
      if (col < item.x + item.w && row < item.y + item.h) {
        continue;
      }
      // Validate array bounds before access (occupiedCells may not match GRID_MAX_ROWS x GRID_COLS)
      if (row >= occupiedCells.length || col >= (occupiedCells[row]?.length ?? 0)) {
        return false;
      }
      // Check if cell is occupied by another item
      if (occupiedCells[row][col]) {
        return false;
      }
    }
  }

  return true;
}

export function useGridResize({
  item,
  occupiedCells,
  onResizeComplete,
}: UseGridResizeOptions): UseGridResizeReturn {
  const [isResizing, setIsResizing] = useState(false);
  const [previewSize, setPreviewSize] = useState<{ w: number; h: number } | null>(null);

  const handleResizeStart = useCallback(() => {
    setIsResizing(true);
    setPreviewSize({ w: item.w, h: item.h });
  }, [item.w, item.h]);

  const handleResize = useCallback(
    (deltaX: number, deltaY: number) => {
      // Convert pixel delta to cell delta (round to nearest cell)
      const cellDeltaW = Math.round(deltaX / CELL_WIDTH);
      const cellDeltaH = Math.round(deltaY / CELL_HEIGHT);

      // Calculate new size
      const newW = Math.max(1, item.w + cellDeltaW);
      const newH = Math.max(1, item.h + cellDeltaH);

      // Only update preview if the new size is valid
      if (isResizeValid(item, newW, newH, occupiedCells)) {
        setPreviewSize({ w: newW, h: newH });
      }
    },
    [item, occupiedCells]
  );

  const handleResizeEnd = useCallback(
    (deltaX: number, deltaY: number) => {
      const cellDeltaW = Math.round(deltaX / CELL_WIDTH);
      const cellDeltaH = Math.round(deltaY / CELL_HEIGHT);

      const newW = Math.max(1, item.w + cellDeltaW);
      const newH = Math.max(1, item.h + cellDeltaH);

      // Apply resize if valid
      if (isResizeValid(item, newW, newH, occupiedCells)) {
        onResizeComplete(item.i, newW, newH);
      }

      setIsResizing(false);
      setPreviewSize(null);
    },
    [item, occupiedCells, onResizeComplete]
  );

  return {
    isResizing,
    previewSize,
    handleResizeStart,
    handleResize,
    handleResizeEnd,
  };
}
